import orgInfoTemplate from './orgInfo.html';
import './orgInfo.scss';

/*******************************************************************************
 * @ngdoc component
 * @name  csm.books.InitialSetup.dom.component:orgInfo
 * @param {string}   nameLabel    Organization name input label.
 * @param {expr)     onLoad       Evaluated when component is loaded.
 * @param {element=} afterAddress Element to insert after name/address.
 * @param {element=} afterBooks   Element to insert after books info.
 *
 * @description
 * Organization information panel. Evaluates `onLoad` and passes `$validator` as
 * a method that validates the input and returns true if successful.
 *
 * `afterAddress` and `afterBooks` are transcluded after the Address and Opening
 * Books Information, respectively, and `$infoCtrl` is injected into their scope
 * as a reference to this component's controller.
 *******************************************************************************/
export const orgInfoComponent = {
  bindings: { nameLabel: '@', onLoad: '&' },
  template: orgInfoTemplate,
  controller: Controller,
  transclude: {
    afterAddress: '?afterAddress',
    afterBooks: '?afterBooks'
  }
};

/*******************************************************************************
 * Controller
 *******************************************************************************/
/* @ngInject */ function Controller($element, $injector, $transclude, CompilerService) {
  var $ctrl = this;
  var $svcs = { $ctrl, $element, $injector, $transclude };

  $ctrl.$onInit = function () {
    $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs}));

    /*--------------------------------------*/
    /* Insert transcluded content and       */
    /* inject reference to this controller. */
    /*--------------------------------------*/
    CompilerService.transcludeSlots($element, { $infoCtrl: $ctrl }, $transclude);
  };
}

/*****************************************************************************
 * initCtrl */
/**
 * $ctrl property initializer.
 *
 * @param  $svcs  Properties to pass with `$injector.invoke()`.
 *****************************************************************************/
/* @ngInject */ function initCtrl($svcs, $ctrl, $injector, AppData) {
  Object.assign($ctrl, {
    orgInfo: AppData.orgInfo,
    validators: {}, // Content Validators. Populated by csmValidators directive.

    /*----------*/
    /* Handlers */
    /*----------*/
    validateOpenDate: $injector.invoke(validateOpenDate, null, $svcs),
    validatePanel: $injector.invoke(validatePanel, null, $svcs)
  });
  $ctrl.onLoad({ $validator: $ctrl.validatePanel });
}

/*****************************************************************************
 * controller.validateOpenDate */
/**
 * OpenDate input validator. Opening Date is required and can be no more than
 * one year prior to contract start date.
 *
 * @return {string}  Error message, `null`=successful.
 *****************************************************************************/
/* @ngInject */ function validateOpenDate($ctrl, AppData, AppTools) {
  return function ($value) {
    if (isNaN(Date.parse($value))) return 'Must be a valid date';

    var openDate = $value;
    var contDate = $ctrl.orgInfo.contractDate;
    var minMonth = AppData.fyLater
      ? AppData.setupTYStartMonth
      : AppData.setupFYStartMonth;
    var year =
      contDate.getMonth() >= minMonth
        ? contDate.getFullYear()
        : contDate.getFullYear - 1;
    var minDate = new Date(year - 1, minMonth, 1);

    if (openDate < minDate)
      return 'Must be on or after ' + AppTools.dateString(minDate);

    return null;
  };
}

/*****************************************************************************
 * controller.validatePanel */
/**
 * Called by main app to validate the panel before moving to the next view.
 * Runs the input field validators.
 *
 * @return {boolean}  `true`=validation successful.
 *****************************************************************************/
/* @ngInject */ function validatePanel($ctrl) {
  return function () {
    $ctrl.validators.reset();
    return $ctrl.validators.validate(true);
  };
}
